import { useGetLijnnummerForTreinserie } from "@/entities/treinserie";
import { useFeatureFlagValue } from "@/features/config";
import { Badge, BadgeVariant } from "@/shared/components";
import { cn } from "@/shared/utils";
import * as TooltipPrimitive from "@radix-ui/react-tooltip";
import { ReactNode } from "react";

type TreinserieDisplayProps = {
    className?: string;
    /* The treinserie or treinnummer */
    nummer: string | number;
    variant?: "tooltip" | "badge" | "justified";
    badgeVariantOverride?: BadgeVariant;
};

export function TreinserieDisplay({ className, nummer, badgeVariantOverride, variant = "tooltip" }: TreinserieDisplayProps) {
    const lijnnummersActief = useFeatureFlagValue("LIJNNUMMERS_ACTIEF", false);
    const { data: lijnnummer, isPending } = useGetLijnnummerForTreinserie(nummer);
    const badgeVariant = badgeVariantOverride ?? "gray-subtle";

    if (isPending && lijnnummersActief) return;

    if (!lijnnummer || !lijnnummersActief) {
        return (
            <span data-testid="treinserie-nummer" className={className}>
                {nummer}
            </span>
        );
    }

    if (variant === "tooltip") {
        return (
            <TreinserieTooltip lijnnummer={lijnnummer}>
                <span data-testid="treinserie-nummer" tabIndex={0} className={cn("underline decoration-dotted", className)}>
                    {nummer}
                </span>
            </TreinserieTooltip>
        );
    }

    if (variant === "badge") {
        return (
            <span className={cn("flex shrink flex-row items-center gap-2", className)}>
                <span data-testid="treinserie-nummer">{nummer}</span>
                <Badge data-testid="treinserie-lijnnummer" className="rounded-s font-bold" variant={badgeVariant}>
                    {lijnnummer}
                </Badge>
            </span>
        );
    }

    return (
        <div className={cn("flex w-full flex-row items-center justify-between", className)}>
            <span data-testid="treinserie-nummer">{nummer}</span>
            {lijnnummer && (
                <Badge className="rounded-s font-bold" size="s" variant={badgeVariant} data-testid="treinserie-lijnnummer">
                    {lijnnummer}
                </Badge>
            )}
        </div>
    );
}

type TreinserieTooltipProps = {
    lijnnummer: string | number;
    children: ReactNode;
};

function TreinserieTooltip({ children, lijnnummer }: TreinserieTooltipProps) {
    return (
        <TooltipPrimitive.Provider data-slot="tooltip-provider" delayDuration={0}>
            <TooltipPrimitive.Root data-slot="tooltip">
                <TooltipPrimitive.Trigger asChild>{children}</TooltipPrimitive.Trigger>
                <TooltipPrimitive.Portal>
                    <TooltipPrimitive.Content
                        asChild
                        data-slot="tooltip-content"
                        align="center"
                        avoidCollisions={false}
                        className="animate-in fade-in-0 zoom-in-95 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95 data-[side=bottom]:slide-in-from-top-2 z-100 origin-(--radix-tooltip-content-transform-origin)"
                        side="bottom"
                        sideOffset={2}
                    >
                        <div className="bg-default">
                            <Badge data-testid="treinserie-lijnnummer" variant="gray-subtle" className="rounded-s font-medium">
                                {lijnnummer}
                            </Badge>
                        </div>
                    </TooltipPrimitive.Content>
                </TooltipPrimitive.Portal>
            </TooltipPrimitive.Root>
        </TooltipPrimitive.Provider>
    );
}
